import { useId, type ReactNode } from 'react';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Input } from '@voll-intelligence/ui-kit/ui/input';
import { RadioGroup, RadioGroupItem } from '@voll-intelligence/ui-kit/ui/radio-group';
import { Textarea } from '@voll-intelligence/ui-kit/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@voll-intelligence/ui-kit/ui/toggle-group';
import { FIELD_CONTROL_SIZE, FieldShell } from '../../shared/field/field_shell.js';
import { StaticSelectField } from '../../shared/field/static_select_field.js';
import { unhandledCase } from './unhandled_case.js';

interface RequestOption {
  value: string;
  label: string;
}

/** As restrições e o período por trecho — o que só a pessoa sabe sobre o voo. */
export interface RequestFlightAsks {
  kind: 'flight';
  copy: { title: string; direct: string; luggage: string; periodAny: string };
  onlyDirect: boolean;
  onlyWithLuggage: boolean;
  onRestrictionsChange: (next: { onlyDirect: boolean; onlyWithLuggage: boolean }) => void;
  /** Um período por trecho; `label` já nomeia o trecho e a data. Valor vazio =
   *  indiferente. */
  segments: readonly { key: string; label: string; value: string }[];
  periodOptions: RequestOption[];
  onPeriodChange: (index: number, value: string) => void;
}

/** O hotel preferido (texto livre) e o tipo de cama. */
export interface RequestHotelAsks {
  kind: 'hotel';
  copy: {
    preferredHotel: string;
    preferredHotelPlaceholder: string;
    bedType: string;
    bedAny: string;
  };
  preferredHotel: string;
  onPreferredHotelChange: (value: string) => void;
  /** Vazio = indiferente. */
  bedType: string;
  bedOptions: RequestOption[];
  onBedTypeChange: (value: string) => void;
}

/** As preferências do ônibus — todas obrigatórias no pedido (o app as exige):
 *  um período por perna e onde sentar. Cada campo já vem com o rótulo e as
 *  opções; valor vazio = ainda não escolhido. */
export interface RequestBusAsks {
  kind: 'bus';
  copy: { title: string; choose: string };
  fields: readonly {
    key: string;
    label: string;
    value: string;
    options: RequestOption[];
    onChange: (value: string) => void;
  }[];
}

/** A categoria (obrigatória, poucas e fixas — chips de escolha única) e a
 *  locadora (opcional). Os catálogos vêm do fornecedor: enquanto chegam, ou se
 *  falharem, a escolha não existe — sem categoria não há pedido, então a falha
 *  oferece tentar de novo em vez de seguir sem ela. */
export interface RequestRentalAsks {
  kind: 'rental';
  copy: {
    category: string;
    company: string;
    companyAny: string;
    loading: string;
    failed: string;
    retry: string;
  };
  options:
    | { phase: 'loading' }
    | { phase: 'failed'; onRetry: () => void }
    | {
        phase: 'ready';
        categories: RequestOption[];
        /** Vazia = sem lista de locadoras (o campo some; ela é opcional). */
        companies: RequestOption[];
      };
  category: string;
  onCategoryChange: (value: string) => void;
  /** Vazio = indiferente. */
  company: string;
  onCompanyChange: (value: string) => void;
}

/** "Por que pedir à equipe": `absent` quando o catálogo não respondeu (o pedido segue
 *  sem motivo). */
export type RequestReason =
  | { phase: 'absent' }
  | { phase: 'loading'; title: string; loading: string }
  | {
      phase: 'ready';
      title: string;
      options: RequestOption[];
      value: string;
      onChange: (value: string) => void;
    };

export interface RequestEditorCopy {
  title: string;
  noPriceBadge: string;
  profileLabel: string;
  profileHint: string;
  optional: string;
  detailsLabel: string;
  detailsPlaceholder: string;
  /** Por que os detalhes são obrigatórios, quando são. */
  detailsHint?: string;
}

export interface RequestEditorProps {
  /** O perfil herdado da busca, em chips travados. */
  profile: readonly string[];
  asks: RequestFlightAsks | RequestHotelAsks | RequestBusAsks | RequestRentalAsks;
  reason: RequestReason;
  details: {
    value: string;
    maxLength: number;
    required: boolean;
    onChange: (value: string) => void;
  };
  copy: RequestEditorCopy;
}

/**
 * O cartão do pedido à equipe VOLL: o perfil herdado da busca (chips travados), o que
 * a pessoa acrescenta (restrições do voo / hotel preferido e cama / onde sentar no
 * ônibus / categoria e locadora do carro), por que pedir à
 * equipe e a observação livre. Vive ACIMA dos viajantes e do motivo porque é a única
 * parte do checkout que ainda não está preenchida.
 */
export function RequestEditor({
  profile,
  asks,
  reason,
  details,
  copy,
}: Readonly<RequestEditorProps>): ReactNode {
  const detailsId = useId();
  return (
    <section
      className="genui2 @container bg-card border-border grid gap-4 rounded-xl border p-5 shadow-xs"
      aria-label={copy.title}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-title m-0 font-semibold">{copy.title}</h3>
        <Badge variant="soft" color="warning">
          {copy.noPriceBadge}
        </Badge>
      </div>
      {profile.length > 0 && (
        <div className="grid gap-1.5">
          <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0" aria-label={copy.profileLabel}>
            {profile.map((chip, index) => (
              <LockedChip key={`${String(index)}-${chip}`}>{chip}</LockedChip>
            ))}
          </ul>
          <span className="text-caption text-muted-foreground">{copy.profileHint}</span>
        </div>
      )}
      <Asks asks={asks} optional={copy.optional} />
      <ReasonField reason={reason} />
      <div className="grid gap-1.5">
        <label htmlFor={detailsId} className="contents">
          <SectionLabel
            optional={details.required ? undefined : copy.optional}
            trailing={
              <span className="text-caption text-muted-foreground tabular-nums">
                {details.value.length}/{details.maxLength}
              </span>
            }
          >
            {copy.detailsLabel}
          </SectionLabel>
        </label>
        <Textarea
          id={detailsId}
          rows={3}
          value={details.value}
          maxLength={details.maxLength}
          required={details.required}
          placeholder={copy.detailsPlaceholder}
          onChange={(event) => {
            details.onChange(event.target.value);
          }}
        />
        {copy.detailsHint && (
          <span className="text-caption text-muted-foreground">{copy.detailsHint}</span>
        )}
      </div>
    </section>
  );
}

/** Chip do perfil herdado — só leitura, superfície `muted` (não é escolha). */
function LockedChip({ children }: { readonly children: string }): ReactNode {
  return (
    <li className="bg-muted text-muted-foreground inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap">
      {children}
    </li>
  );
}

function SectionLabel({
  children,
  optional,
  trailing,
}: {
  readonly children: string;
  readonly optional?: string;
  readonly trailing?: ReactNode;
}): ReactNode {
  return (
    <span className="text-ui flex items-baseline gap-1.5 font-medium">
      {children}
      {optional && (
        <span className="text-caption text-muted-foreground font-normal">{optional}</span>
      )}
      {trailing && <span className="ml-auto">{trailing}</span>}
    </span>
  );
}

const FLIGHT_RESTRICTIONS = ['direct', 'luggage'] as const;

function FlightAsks({
  asks,
  optional,
}: {
  readonly asks: RequestFlightAsks;
  readonly optional: string;
}): ReactNode {
  const value = [
    ...(asks.onlyDirect ? ['direct'] : []),
    ...(asks.onlyWithLuggage ? ['luggage'] : []),
  ];
  return (
    <div className="grid gap-3">
      <div className="grid gap-2">
        <SectionLabel optional={optional}>{asks.copy.title}</SectionLabel>
        <ToggleGroup
          multiple
          variant="outline"
          size="sm"
          className="flex-wrap"
          value={value}
          aria-label={asks.copy.title}
          onValueChange={(next) => {
            const picked = new Set(next);
            asks.onRestrictionsChange({
              onlyDirect: picked.has('direct'),
              onlyWithLuggage: picked.has('luggage'),
            });
          }}
        >
          {FLIGHT_RESTRICTIONS.map((key) => (
            <ToggleGroupItem key={key} value={key} className="rounded-full">
              {asks.copy[key]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <div className="grid gap-3 @min-[theme(--breakpoint-sm)]:grid-cols-2">
        {asks.segments.map((segment, index) => (
          <StaticSelectField
            key={segment.key}
            label={segment.label}
            value={segment.value}
            options={asks.periodOptions}
            placeholder={asks.copy.periodAny}
            onChange={(next) => {
              asks.onPeriodChange(index, next);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function Asks({
  asks,
  optional,
}: {
  readonly asks: RequestEditorProps['asks'];
  readonly optional: string;
}): ReactNode {
  switch (asks.kind) {
    case 'flight':
      return <FlightAsks asks={asks} optional={optional} />;
    case 'hotel':
      return <HotelAsks asks={asks} />;
    case 'bus':
      return <BusAsks asks={asks} />;
    case 'rental':
      return <RentalAsks asks={asks} optional={optional} />;
    default:
      return unhandledCase(asks);
  }
}

function BusAsks({ asks }: { readonly asks: RequestBusAsks }): ReactNode {
  return (
    <div className="grid gap-2">
      <SectionLabel>{asks.copy.title}</SectionLabel>
      <div className="grid gap-3 @min-[theme(--breakpoint-sm)]:grid-cols-2">
        {asks.fields.map((field) => (
          <StaticSelectField
            key={field.key}
            label={field.label}
            value={field.value}
            options={field.options}
            placeholder={asks.copy.choose}
            onChange={field.onChange}
          />
        ))}
      </div>
    </div>
  );
}

function HotelAsks({ asks }: { readonly asks: RequestHotelAsks }): ReactNode {
  const hotelId = useId();
  return (
    <div className="grid gap-3 @min-[theme(--breakpoint-sm)]:grid-cols-[2fr_1fr]">
      <FieldShell label={asks.copy.preferredHotel} fieldId={hotelId}>
        <Input
          id={hotelId}
          className={FIELD_CONTROL_SIZE}
          value={asks.preferredHotel}
          placeholder={asks.copy.preferredHotelPlaceholder}
          onChange={(event) => {
            asks.onPreferredHotelChange(event.target.value);
          }}
        />
      </FieldShell>
      <StaticSelectField
        label={asks.copy.bedType}
        value={asks.bedType}
        options={asks.bedOptions}
        placeholder={asks.copy.bedAny}
        onChange={asks.onBedTypeChange}
      />
    </div>
  );
}

function ReasonField({ reason }: { readonly reason: RequestReason }): ReactNode {
  const baseId = useId();
  if (reason.phase === 'absent') return null;
  return (
    <fieldset className="m-0 grid gap-2 border-0 p-0">
      <legend className="text-ui mb-2 p-0 font-medium">{reason.title}</legend>
      {reason.phase === 'loading' ? (
        <p className="text-caption text-muted-foreground m-0" role="status">
          {reason.loading}
        </p>
      ) : (
        <RadioGroup
          value={reason.value}
          className="gap-2.5 @min-[theme(--breakpoint-sm)]:grid-cols-2"
          onValueChange={(next) => {
            if (typeof next === 'string') reason.onChange(next);
          }}
        >
          {reason.options.map((option, index) => {
            const id = `${baseId}-${String(index)}`;
            return (
              <label key={option.value} htmlFor={id} className="text-ui flex items-center gap-2.5">
                <RadioGroupItem id={id} value={option.value} />
                <span className="min-w-0">{option.label}</span>
              </label>
            );
          })}
        </RadioGroup>
      )}
    </fieldset>
  );
}

function RentalAsks({
  asks,
  optional,
}: {
  readonly asks: RequestRentalAsks;
  readonly optional: string;
}): ReactNode {
  const { options } = asks;
  if (options.phase === 'loading') {
    return (
      <p className="text-caption text-muted-foreground m-0" role="status">
        {asks.copy.loading}
      </p>
    );
  }
  if (options.phase === 'failed') {
    return (
      <div className="flex flex-wrap items-center gap-2" role="alert">
        <span className="text-caption text-muted-foreground min-w-0 flex-1">
          {asks.copy.failed}
        </span>
        <Button type="button" variant="outline" size="sm" onClick={options.onRetry}>
          {asks.copy.retry}
        </Button>
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      <div className="grid gap-2">
        <SectionLabel>{asks.copy.category}</SectionLabel>
        {/* Escolha única e obrigatória: tocar de novo no chip marcado não o
            desmarca (o grupo devolveria vazio). */}
        <ToggleGroup
          variant="outline"
          size="sm"
          className="flex-wrap"
          value={asks.category ? [asks.category] : []}
          aria-label={asks.copy.category}
          onValueChange={(next) => {
            const [picked] = next;
            if (picked) asks.onCategoryChange(picked);
          }}
        >
          {options.categories.map((category) => (
            <ToggleGroupItem key={category.value} value={category.value} className="rounded-full">
              {category.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      {options.companies.length > 0 && (
        <div className="grid gap-3 @min-[theme(--breakpoint-sm)]:grid-cols-2">
          <StaticSelectField
            label={`${asks.copy.company} (${optional})`}
            value={asks.company}
            options={options.companies}
            placeholder={asks.copy.companyAny}
            onChange={asks.onCompanyChange}
          />
        </div>
      )}
    </div>
  );
}
